import { FC, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analytics } from '@app/app/analytics';
import { PlatformName } from '@app/app/analytics/types';
import { useAppSelector } from '@app/hooks/use-store';

/** Ключ LS с timestamp (мс) последнего закрытия баннера. */
const DISMISSED_AT_KEY = 'new-version-banner-dismissed-at';

/** TTL закрытия: через 7 дней после закрытия баннер показываем снова. */
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Скрыт ли баннер по cooldown: считаем один раз при монтировании (для 7-дневного TTL
 * этого достаточно — при следующей загрузке/навигации значение пересчитается).
 * Скрыт, если закрывали и с момента закрытия прошло меньше {@link DISMISS_TTL_MS}.
 */
function isWithinDismissTtl(): boolean {
  const raw = localStorage.getItem(DISMISSED_AT_KEY);
  const dismissedAt = Number(raw);
  if (!raw || !Number.isFinite(dismissedAt)) return false;
  return Date.now() - dismissedAt < DISMISS_TTL_MS;
}

/**
 * Баннер новой версии WonderSlide с AI-агентами.
 * Зафиксирован сверху (position: fixed) — не теряется при скролле,
 * оформлен яркой alert-подсветкой (оранж/амбер + неоновое свечение).
 * Высота баннера компенсируется отступом сверху на контейнере #root (уровень layout),
 * поэтому контент лендинга и внутренних SPA-страниц не уезжает под баннер.
 * Закрытие снузит баннер на 7 дней (TTL), после чего он показывается снова.
 * Всегда ведёт на новый продукт.
 */
export const NewVersionBanner: FC = () => {
  const { t } = useTranslation('common');
  const isAuthenticated = useAppSelector((state) => state.authState.isAuthenticated);
  const [dismissed, setDismissed] = useState(isWithinDismissTtl);

  const bannerRef = useRef<HTMLDivElement>(null);
  const isVisible = !dismissed && !IS_BOXED;

  // Компенсируем высоту фиксированного баннера отступом сверху на контейнере #root
  // (не на body: у него height:100% + overflow-y:auto, padding там ломает раскладку).
  // Так контент и статики лендинга, и внутренних SPA-страниц не уезжает под баннер.
  // ResizeObserver — на случай переноса текста на узких экранах (высота меняется).
  useLayoutEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    if (!isVisible) {
      root.style.paddingTop = '';
      return;
    }

    const el = bannerRef.current;
    if (!el) return;

    const applyOffset = () => {
      root.style.paddingTop = `${el.offsetHeight}px`;
    };
    applyOffset();

    const observer = new ResizeObserver(applyOffset);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.paddingTop = '';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  // src=old_main — обычный (не UTM) query-параметр: GTM новой версии читает его
  // и вешает на события, чтобы глава мог измерить переходы со старой версии на новую.
  const href = 'https://wonderslide.com/?src=old_main';

  return (
    <div
      ref={bannerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        width: '100%',
        // симметричные горизонтальные паддинги оставляют место под absolute-кнопку закрытия
        padding: '10px 44px',
        background: 'linear-gradient(90deg, #f59e0b, #f97316, #ef4444)',
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        textShadow: '0 1px 2px rgba(0,0,0,0.25)',
        borderBottom: '1px solid rgba(255,255,255,0.35)',
        // выше хедера (z-100) и контента; мобильное меню (z-300) при этом перекрывает баннер
        zIndex: 200,
        animation: 'nv-banner-glow 2.4s ease-in-out infinite',
      }}
    >
      <style>{`
        @keyframes nv-banner-glow {
          0%, 100% { box-shadow: 0 0 14px rgba(249,115,22,0.55), 0 2px 10px rgba(0,0,0,0.25); }
          50%      { box-shadow: 0 0 30px rgba(249,115,22,0.95), 0 2px 10px rgba(0,0,0,0.25); }
        }
      `}</style>
      <span style={{ textAlign: 'center' }}>🚀 {t('newVersionBanner.text')}</span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          analytics.emitEvent('click_new_version_banner', {
            [PlatformName.GTM]: {
              is_authenticated: isAuthenticated,
              target_url: href,
            },
          });
        }}
        style={{
          background: '#fff',
          color: '#c2410c',
          padding: '4px 14px',
          borderRadius: 6,
          textDecoration: 'none',
          fontSize: 13,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          textShadow: 'none',
          boxShadow: '0 0 12px rgba(255,255,255,0.6)',
        }}
      >
        {t('newVersionBanner.cta')} →
      </a>
      <button
        aria-label="Close"
        onClick={() => {
          analytics.emitEvent('dismiss_new_version_banner', {
            [PlatformName.GTM]: {
              is_authenticated: isAuthenticated,
            },
          });
          setDismissed(true);
          localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
        }}
        style={{
          position: 'absolute',
          right: 12,
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.85)',
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 4,
          textShadow: 'none',
        }}
      >
        ✕
      </button>
    </div>
  );
};
